/**
 * Application List Component
 * Displays a list of applications with actions
 */

import React from 'react';
import { ModernTable, TableHeader, TableBody, TableRow, TableCell } from '../common/ModernTable';
import { APPLICATION_STATUS } from '../../config/constants';
import './ApplicationList.css';

const ApplicationList = ({
  applications,
  isLoading,
  isProcessing,
  selectedApplications,
  onApplicationSelect,
  onSelectAll,
  onEditApplication,
  onViewApplication,
  onDeleteApplication,
  onReviewApplication,
  pagination,
  onPageChange,
  onPageSizeChange,
  canManageApplications,
  canApproveApplications,
  canDeleteApplications
}) => {
  // Table columns configuration
  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={applications.length > 0 && selectedApplications.length === applications.length}
          onChange={(e) => onSelectAll(e.target.checked)}
          disabled={isProcessing}
        />
      ),
      render: (application) => (
        <input
          type="checkbox"
          checked={selectedApplications.includes(application.id)}
          onChange={(e) => onApplicationSelect(application.id, e.target.checked)}
          disabled={isProcessing}
        />
      ),
      width: '50px'
    },
    {
      key: 'member_id',
      header: 'Member ID',
      render: (application) => (
        <span className="member-id">
          {application.member_id || 'N/A'}
        </span>
      ),
      sortable: true
    },
    {
      key: 'organization_name',
      header: 'Organization',
      render: (application) => (
        <div className="organization-info">
          <div className="organization-name">
            {application.organization_name}
          </div>
          <div className="organization-details">
            {application.city && application.state && (
              <span className="location">
                {application.city}, {application.state}
              </span>
            )}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'president_name',
      header: 'Contact Person',
      render: (application) => (
        <div className="contact-info">
          <div className="contact-name">
            {application.president_name}
          </div>
          <div className="contact-email">
            {application.president_email}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'status',
      header: 'Status',
      render: (application) => (
        <span className={`status-badge status-${application.status}`}>
          {application.status_display || application.status}
        </span>
      ),
      sortable: true
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (application) => (
        <span className="date">
          {new Date(application.created_at).toLocaleDateString()}
        </span>
      ),
      sortable: true
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (application) => (
        <div className="action-buttons">
          <button
            className="btn btn-sm btn-outline"
            onClick={() => onViewApplication(application)}
            title="View Details"
          >
            👁️
          </button>
          
          {canManageApplications && (
            <button
              className="btn btn-sm btn-outline"
              onClick={() => onEditApplication(application)}
              title="Edit Application"
            >
              ✏️
            </button>
          )}
          
          {canApproveApplications && application.status === APPLICATION_STATUS.PENDING && (
            <>
              <button
                className="btn btn-sm btn-success"
                onClick={() => onReviewApplication(application.id, { status: APPLICATION_STATUS.APPROVED })}
                title="Approve Application"
              >
                ✅
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => onReviewApplication(application.id, { status: APPLICATION_STATUS.REJECTED })}
                title="Reject Application"
              >
                ❌
              </button>
            </>
          )}
          
          {canDeleteApplications && (
            <button
              className="btn btn-sm btn-danger"
              onClick={() => onDeleteApplication(application.id)}
              title="Delete Application"
            >
              🗑️
            </button>
          )}
        </div>
      ),
      width: '200px'
    }
  ];

  if (isLoading) {
    return (
      <div className="application-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="application-list">
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Applications Found</h3>
          <p>No applications match your current filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-list">
      <ModernTable className="applications-table">
        <TableHeader>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={index} className={column.className} style={{ width: column.width }}>
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {applications.map((application) => (
            <TableRow key={application.id} className="application-row">
              {columns.map((column, index) => (
                <TableCell key={index} className={column.className}>
                  {column.render ? column.render(application) : application[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </ModernTable>
      
      {/* Pagination */}
      {pagination && (
        <div className="table-pagination">
          <div className="pagination-info">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.totalCount || applications.length)} of {pagination.totalCount || applications.length} applications
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-outline btn-sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              Previous
            </button>
            <span className="pagination-page">
              Page {pagination.page} of {pagination.totalPages || 1}
            </span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= (pagination.totalPages || 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationList;
